import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for dynamic page metadata
 * 
 * Usage:
 * <SEO 
 *   title="Page Title"
 *   description="Page description for search engines"
 *   path="/page-path"
 * />
 */
const SEO = ({ 
  title, 
  description, 
  path = '/',
  type = 'website',
  image = null 
}) => {
  const baseUrl = 'https://sponsiq.com';
  const fullUrl = `${baseUrl}${path}`;
  const defaultImage = `${baseUrl}/og-image.png`;
  
  // Site name suffix
  const fullTitle = title 
    ? `${title} | Sponsiq`
    : 'Sponsiq – Discover Brands Sponsoring YouTube Creators';
  
  const defaultDescription = 'Sponsorship intelligence platform for YouTube creators. Discover brands actively sponsoring creators, track brand mentions, and find sponsorship opportunities based on real data.';
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description || defaultDescription} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description || defaultDescription} />
      <meta property="og:image" content={image || defaultImage} />
      <meta property="og:site_name" content="Sponsiq" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={fullUrl} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description || defaultDescription} />
      <meta property="twitter:image" content={image || defaultImage} />
    </Helmet>
  );
};

export default SEO;

// Pre-defined SEO configs for each page
export const PAGE_SEO = {
  home: {
    title: null, // Uses default
    description: 'Sponsiq – Sponsorship intelligence platform for YouTube creators. Discover brands actively sponsoring creators, track brand mentions, and find sponsorship opportunities based on real data.',
    path: '/'
  },
  brandsDiscover: {
    title: 'Discover YouTube Creators for Sponsorships',
    description: 'Find YouTube creators ready for brand partnerships. Browse creators by niche, subscriber count, and engagement metrics.',
    path: '/brands/discover'
  },
  exploreBrands: {
    title: 'Explore Sponsoring Brands',
    description: 'Discover brands actively sponsoring YouTube creators. Find potential sponsors for your channel across tech, finance, lifestyle and more.',
    path: '/explore-brands'
  },
  leaderboards: {
    title: 'Creator Leaderboards',
    description: 'Top YouTube creators ranked by subscriber count and sponsorship potential. Find the most influential creators in each niche.',
    path: '/leaderboards'
  },
  forBrands: {
    title: 'For Brands – Find YouTube Creators',
    description: 'Connect with YouTube creators for authentic sponsored content. Discover creators that match your brand and reach your target audience.',
    path: '/for-brands'
  },
  forCreators: {
    title: 'For Creators – Get Sponsored',
    description: 'Find brands looking to sponsor YouTube creators like you. Get your sponsorship readiness score and connect with potential sponsors.',
    path: '/for-creators'
  },
  terms: {
    title: 'Terms of Service',
    description: 'Sponsiq Terms of Service. Read our terms and conditions for using the platform.',
    path: '/terms'
  },
  privacy: {
    title: 'Privacy Policy',
    description: 'Sponsiq Privacy Policy. Learn how we collect, use, and protect your data.',
    path: '/privacy'
  }
};
